import { ref, computed, toValue, nextTick } from "vue";
import { computedWithControl } from "@vueuse/core";
import { mapValue, interpolate } from "@maas/vue-equipment/utils";
import { useMagicEmitter } from "@maas/vue-equipment/plugins";
export function useDrawerSnap(args) {
  const {
    id,
    snapPoints,
    animation,
    position,
    wrapperRect,
    overshoot,
    draggedY,
    draggedX,
    preventDragClose
  } = args;
  const mappedSnapPoints = computedWithControl(
    () => toValue(wrapperRect),
    () => {
      const extended = toValue(preventDragClose) ? toValue(snapPoints) : [...toValue(snapPoints), 0];
      const mapped = extended.map((snapPoint) => {
        return mapSnapPoint(snapPoint);
      });
      const filtered = mapped.filter(
        (snapPoint) => !!snapPoint || snapPoint === 0
      ).sort((a, b) => a - b);
      return filtered;
    }
  );
  const snapPointsMap = computedWithControl(
    () => toValue(snapPoints),
    () => {
      const extended = toValue(preventDragClose) ? toValue(snapPoints) : [...toValue(snapPoints), 0];
      const mapped = extended.reduce(
        (acc, current) => {
          const key = mapSnapPoint(current);
          if (key || key === 0) {
            acc[key] = current;
          }
          return acc;
        },
        {}
      );
      return mapped;
    }
  );
  const snappedY = ref(0);
  const snappedX = ref(0);
  const activeSnapPoint = ref(void 0);
  const drawerHeight = computed(() => {
    const rect = toValue(wrapperRect);
    if (rect === void 0) {
      return 0;
    }
    return rect.height - toValue(overshoot);
  });
  const drawerWidth = computed(() => {
    const rect = toValue(wrapperRect);
    if (rect === void 0) {
      return 0;
    }
    return rect.width - toValue(overshoot);
  });
  const emitter = useMagicEmitter();
  function findClosestNumber(args2) {
    const { number, numbers, direction } = args2;
    let filtered = numbers;
    if (!numbers.length) {
      return void 0;
    }
    switch (direction) {
      case "above":
        filtered = numbers.filter((num) => num > number);
        break;
      case "below":
        filtered = numbers.filter((num) => num < number);
        break;
    }
    if (filtered.length === 0) {
      switch (direction) {
        case "above":
          return Math.max(...numbers);
        case "below":
          return Math.min(...numbers);
        default:
          return void 0;
      }
    }
    const closestNumber = filtered.reduce(
      (closest, current) => Math.abs(current - number) < Math.abs(closest - number) ? current : closest
    );
    return closestNumber;
  }
  function mapSnapPoint(snapPoint) {
    if (typeof snapPoint === "number") {
      const reversedSnapPoint = mapValue(snapPoint, 0, 1, 1, 0);
      const vh = window.innerHeight;
      const vw = window.innerWidth;
      if (toValue(wrapperRect) === void 0) {
        return void 0;
      }
      switch (position) {
        case "bottom": {
          const rect = toValue(wrapperRect);
          if (reversedSnapPoint === 1) return drawerHeight.value;
          if (reversedSnapPoint === 0) return 0;
          if (!rect) return 0;
          return vh * reversedSnapPoint - rect.top;
        }
        case "top": {
          const rect = toValue(wrapperRect);
          if (reversedSnapPoint === 1) return drawerHeight.value * -1;
          if (reversedSnapPoint === 0) return 0;
          if (!rect) return 0;
          return vh * reversedSnapPoint - rect.bottom;
        }
        case "right": {
          const rect = toValue(wrapperRect);
          if (reversedSnapPoint === 1) return drawerWidth.value;
          if (reversedSnapPoint === 0) return 0;
          if (!rect) return 0;
          return vw * reversedSnapPoint - rect.left;
        }
        case "left": {
          const rect = toValue(wrapperRect);
          if (reversedSnapPoint === 1) return drawerWidth.value * -1;
          if (reversedSnapPoint === 0) return 0;
          if (!rect) return 0;
          return vw * reversedSnapPoint - rect.right;
        }
        default:
          return 0;
      }
    } else {
      const parsedSnapPoint = parseFloat(snapPoint);
      if (!drawerHeight.value || !drawerWidth.value) {
        return void 0;
      }
      switch (position) {
        case "bottom":
          return drawerHeight.value - parsedSnapPoint;
        case "top":
          return (drawerHeight.value - parsedSnapPoint) * -1;
        case "right":
          return drawerWidth.value - parsedSnapPoint;
        case "left":
          return (drawerWidth.value - parsedSnapPoint) * -1;
        default:
          return parseFloat(snapPoint);
      }
    }
  }
  async function snapTo(args2) {
    const { snapPoint, interpolate: interpolate2, duration } = args2;
    await nextTick();
    switch (position) {
      case "top":
      case "bottom": {
        const mappedSnapPointY = mapSnapPoint(snapPoint);
        if (!mappedSnapPointY && mappedSnapPointY !== 0) return;
        const closestY = findClosestSnapPoint({
          draggedX,
          draggedY: mappedSnapPointY
        }) || 0;
        if (interpolate2) {
          interpolateDragged({ to: closestY, duration });
        } else {
          draggedY.value = closestY;
        }
        snappedY.value = closestY;
        activeSnapPoint.value = toValue(snapPoint);
        break;
      }
      case "left":
      case "right": {
        const mappedSnapPointX = mapSnapPoint(toValue(snapPoint));
        if (!mappedSnapPointX && mappedSnapPointX !== 0) return;
        const closestX = findClosestSnapPoint({
          draggedX: mappedSnapPointX,
          draggedY
        }) || 0;
        if (interpolate2) {
          interpolateDragged({ to: closestX, duration });
        } else {
          draggedX.value = closestX;
          snappedX.value = closestX;
        }
        snappedX.value = closestX;
        activeSnapPoint.value = toValue(snapPoint);
        break;
      }
    }
  }
  function interpolateDragged(args2) {
    const {
      to,
      duration = toValue(animation)?.snap?.duration || 300,
      easing
    } = args2;
    const snapPoint = snapPointsMap.value[to];
    emitter.emit("beforeSnap", { id: toValue(id), snapPoint });
    switch (position) {
      case "bottom":
      case "top":
        interpolate({
          from: draggedY.value,
          to,
          duration,
          easing,
          callback: (value) => {
            draggedY.value = value;
            if (draggedY.value === to) {
              emitter.emit("afterSnap", {
                id: toValue(id),
                snapPoint
              });
            }
          }
        });
        break;
      case "right":
      case "left":
        interpolate({
          from: draggedX.value,
          to,
          duration,
          easing,
          callback: (value) => {
            draggedX.value = value;
            if (draggedX.value === to) {
              emitter.emit("afterSnap", {
                id: toValue(id),
                snapPoint
              });
            }
          }
        });
        break;
    }
  }
  function findClosestSnapPoint(args2) {
    const { draggedY: draggedY2, draggedX: draggedX2, direction = "absolute" } = args2;
    let closest = void 0;
    switch (position) {
      case "bottom":
      case "top":
        if (!!toValue(draggedY2) || toValue(draggedY2) === 0) {
          closest = findClosestNumber({
            number: toValue(draggedY2),
            numbers: mappedSnapPoints.value,
            direction
          });
        }
        break;
      case "right":
      case "left":
        if (!!toValue(draggedX2) || toValue(draggedX2) === 0) {
          closest = findClosestNumber({
            number: toValue(draggedX2),
            numbers: mappedSnapPoints.value,
            direction
          });
        }
        break;
    }
    return closest;
  }
  return {
    snappedY,
    snappedX,
    activeSnapPoint,
    snapTo,
    findClosestSnapPoint,
    interpolateDragged,
    snapPointsMap,
    drawerHeight,
    drawerWidth
  };
}
